import { useCallback } from "react";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { useBuildPageStore } from "~/components/build/Show/lib/useBuildPageStore";
import Icon from "~/components/shared/Icon";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
  HoverCardPortal,
} from "~/components/shared/HoverCard";
import SharedBadge from "~/components/shared/Badge";
import { Badge } from "~/components/build/Show/routes/list/-components/Badge";
import { AgentIcon, MACHINE_TYPES } from "~/components/job/Job/Agent";
import { CommandJob, OS } from "~/components/build/Show/lib/types/CommandJob";

function getQueueIcon(hosted: boolean): string {
  if (hosted) {
    return "heroicons/16/solid/cloud";
  }
  return "heroicons/16/solid/server-stack";
}

function QueueIcon({ hosted, os }: { hosted: boolean; os?: OS }) {
  if (hosted && os && os !== "unknown") {
    return <AgentIcon os={os} />;
  }
  return <Icon icon={getQueueIcon(hosted)} className="h-3 w-3 shrink-0" />;
}

type QueueHoverDetailsData = {
  agentName?: string;
  agentPath?: string;
  queueName: string;
  queuePath?: string;
  os?: OS;
  hostedOSVersion?: string;
  architecture?: string;
  system?: string;
};

function getQueueHoverDetails({
  job,
  queueKey,
  queuePath,
  os,
}: {
  job?: CommandJob;
  queueKey: string;
  queuePath?: string;
  os?: OS;
}): QueueHoverDetailsData {
  const shape = job?.instanceShape;
  const hosted = job?.hosted;

  const resolvedOs =
    hosted && shape?.operatingSystem && shape.operatingSystem !== "unknown"
      ? shape.operatingSystem
      : os && os !== "unknown"
        ? os
        : undefined;

  return {
    agentName: job?.agentName || undefined,
    agentPath: job?.agentPath,
    queueName: job?.clusterQueue?.key ?? queueKey,
    queuePath: queuePath ?? job?.agentsPath,
    os: resolvedOs,
    hostedOSVersion: hosted && shape ? job?.hostedOSVersion : undefined,
    architecture: hosted && shape ? shape.architecture : undefined,
    system: hosted && shape ? `${shape.cpu} vCPU · ${shape.memory} GB RAM` : undefined,
  };
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="font-semibold">{label}</dt>
      <dd className="m-0 text-right">{children}</dd>
    </>
  );
}

function QueueHoverDetails({
  agentName,
  agentPath,
  queueName,
  queuePath,
  os,
  hostedOSVersion,
  architecture,
  system,
}: QueueHoverDetailsData) {
  return (
    <dl className="grid grid-cols-[1fr_2fr] items-center gap-x-2.5 gap-y-1.5">
      <DetailRow label="Agent">
        {agentName ? (
          <a href={agentPath} className="max-w-full truncate text-inherit hover:text-inherit">
            {agentName}
          </a>
        ) : (
          <span className="text-sm italic text-gray-600">Unassigned</span>
        )}
      </DetailRow>

      <DetailRow label="Queue">
        {queuePath ? (
          <a href={queuePath} className="max-w-full truncate text-inherit hover:text-inherit">
            {queueName}
          </a>
        ) : (
          <span className="max-w-full truncate">{queueName}</span>
        )}
      </DetailRow>

      {os && (
        <DetailRow label="OS">
          <SharedBadge className="charcoal-300 bg-gray-100">
            <img
              src={MACHINE_TYPES[os].logo}
              width="14"
              height="14"
              style={{ verticalAlign: "text-top" }}
              alt={MACHINE_TYPES[os].label}
            />
            {MACHINE_TYPES[os].label}
            {hostedOSVersion ? ` ${hostedOSVersion}` : ""}
          </SharedBadge>
        </DetailRow>
      )}

      {architecture && (
        <DetailRow label="Architecture">
          <SharedBadge className="charcoal-300 bg-gray-100">{architecture}</SharedBadge>
        </DetailRow>
      )}

      {system && (
        <DetailRow label="System">
          <SharedBadge className="charcoal-300 bg-gray-100">{system}</SharedBadge>
        </DetailRow>
      )}
    </dl>
  );
}

/**
 * Resolves the job for the hover card content.
 * Accepts a direct `job` prop (from JobLabel) or a `latestJobId` (from StepItem)
 * which is looked up from the Zustand store only when the hover card is open.
 */
function useResolvedJob(job?: CommandJob, latestJobId?: string): CommandJob | undefined {
  const storeJob = useBuildPageStore((state) =>
    latestJobId && !job ? (state.jobs.get(latestJobId) as CommandJob | undefined) : undefined,
  );
  return job ?? storeJob;
}

/**
 * Lazy-mounted hover content that only subscribes to the store when rendered.
 */
function LazyQueueHoverContent({
  job,
  latestJobId,
  queueKey,
  queuePath,
  os,
}: {
  job?: CommandJob;
  latestJobId?: string;
  queueKey: string;
  queuePath?: string;
  os?: OS;
}) {
  const resolvedJob = useResolvedJob(job, latestJobId);
  const details = getQueueHoverDetails({ job: resolvedJob, queueKey, queuePath, os });

  return <QueueHoverDetails {...details} />;
}

interface QueueLabelProps {
  queueKey: string;
  os?: OS;
  job?: CommandJob;
  latestJobId?: string;
}

export function QueueLabel({ queueKey, os, job, latestJobId }: QueueLabelProps) {
  const { build } = useBuild();
  const clusterQueue = build.clusterQueues.find((queue) => queue.key === queueKey);
  const hosted = clusterQueue?.hosted ?? false;

  const handleClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  const badge = (
    <Badge className="inline-flex items-center gap-1 group-hover/queue:border-gray-600">
      <QueueIcon hosted={hosted} os={os} />
      {queueKey}
    </Badge>
  );

  const trigger = clusterQueue?.path ? (
    <a
      href={clusterQueue.path}
      onClick={handleClick}
      className="group/queue focus:outline-hidden inline-flex items-center rounded-full text-inherit hover:text-inherit focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500"
    >
      {badge}
    </a>
  ) : (
    <span className="group/queue">{badge}</span>
  );

  return (
    <HoverCard>
      <HoverCardTrigger asChild={true}>{trigger}</HoverCardTrigger>
      <HoverCardPortal>
        <HoverCardContent className="z-30" size="max-lg" onClick={handleClick}>
          <LazyQueueHoverContent
            job={job}
            latestJobId={latestJobId}
            queueKey={queueKey}
            queuePath={clusterQueue?.path}
            os={os}
          />
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
}
